import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { 
  ArrowLeft, Save, Plus, Trash2, GripVertical, Eye, EyeOff,
  Type, Hash, Mail, Phone, Calendar, List, CheckSquare, 
  ToggleLeft, FileText, Settings, ChevronDown, ChevronUp,
  AlertCircle, Check, X, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Texto curto', icon: Type },
  { value: 'textarea', label: 'Texto longo', icon: FileText },
  { value: 'number', label: 'Número', icon: Hash },
  { value: 'email', label: 'E-mail', icon: Mail },
  { value: 'tel', label: 'Telefone', icon: Phone },
  { value: 'date', label: 'Data', icon: Calendar },
  { value: 'select', label: 'Lista de opções', icon: List },
  { value: 'radio', label: 'Escolha única', icon: ToggleLeft },
  { value: 'checkbox', label: 'Múltipla escolha', icon: CheckSquare },
];

const STEPS = [
  { value: 2, label: 'Etapa 2 - Dados do Bebê' },
  { value: 3, label: 'Etapa 3 - Sobre o Sono' },
  { value: 4, label: 'Etapa 4 - Saúde e Contexto' },
];

const AdminFormBuilderPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [expandedStep, setExpandedStep] = useState(2);
  const [showAddField, setShowAddField] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    label: '',
    field_type: 'text',
    placeholder: '',
    required: false,
    step: 2,
    section: '',
    options: []
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/form-config`);
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      toast.error('Erro ao carregar configuração do formulário');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/form-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      
      if (response.ok) {
        toast.success('Configuração salva com sucesso!');
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const addField = async () => {
    if (!newField.label.trim()) {
      toast.error('O campo precisa de um nome');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/form-config/field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newField)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Campo adicionado!');
        await fetchConfig();
        setShowAddField(false);
        setNewField({
          label: '',
          field_type: 'text',
          placeholder: '',
          required: false,
          step: 2,
          section: '',
          options: []
        });
      }
    } catch (error) {
      console.error('Erro ao adicionar campo:', error);
      toast.error('Erro ao adicionar campo');
    }
  };

  const updateField = async (fieldId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/form-config/field/${fieldId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        // Atualizar estado local
        setConfig(prev => ({
          ...prev,
          fields: prev.fields.map(f => 
            (f.id === fieldId || f.field_key === fieldId) ? { ...f, ...updates } : f
          ),
          custom_fields: (prev.custom_fields || []).map(f => 
            f.id === fieldId ? { ...f, ...updates } : f
          )
        }));
        toast.success('Campo atualizado!');
      }
    } catch (error) {
      console.error('Erro ao atualizar campo:', error);
      toast.error('Erro ao atualizar campo');
    }
  };

  const deleteField = async (fieldId) => {
    if (!confirm('Tem certeza que deseja remover este campo?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/form-config/field/${fieldId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Campo removido!');
        await fetchConfig();
      } else {
        toast.error(data.detail || 'Erro ao remover campo');
      }
    } catch (error) {
      console.error('Erro ao remover campo:', error);
      toast.error('Erro ao remover campo');
    }
  };

  const resetConfig = async () => {
    if (!confirm('Isso vai remover todos os campos customizados. Continuar?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/form-config/reset`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Configuração resetada!');
        await fetchConfig();
      }
    } catch (error) {
      console.error('Erro ao resetar:', error);
      toast.error('Erro ao resetar configuração');
    }
  };

  const getFieldsByStep = (step) => {
    const baseFields = (config?.fields || []).filter(f => f.step === step);
    const customFields = (config?.custom_fields || []).filter(f => f.step === step);
    return [...baseFields, ...customFields].sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const getFieldTypeIcon = (type) => {
    const fieldType = FIELD_TYPES.find(t => t.value === type);
    return fieldType ? fieldType.icon : Type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAF7F5] to-[#F5E6E0] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8F7AC5]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF7F5] to-[#F5E6E0]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E8D4D0] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-[#6B5B8C]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-[#30244F]">Editor de Formulário</h1>
              <p className="text-sm text-[#6B5B8C]">Configure os campos do questionário inicial</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={resetConfig}
              className="text-red-500 border-red-200 hover:bg-red-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Resetar
            </Button>
            <Button 
              onClick={saveConfig}
              disabled={saving}
              className="bg-[#8F7AC5] hover:bg-[#7B68A6] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Banner Explicativo - Como Funciona */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-gradient-to-r from-[#8F7AC5]/10 via-[#D98DAE]/10 to-[#FAE0D5]/30 rounded-2xl p-6 border border-[#E8D4D0]"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#8F7AC5] flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#30244F] mb-3">📋 Como funciona o Editor de Formulário</h2>
              
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#8F7AC5] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                    <p className="text-[#6B5B8C]"><strong className="text-[#30244F]">Campos Base:</strong> São os campos padrão do questionário. Você pode ativar/desativar e editar, mas não pode deletar.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#8F7AC5] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                    <p className="text-[#6B5B8C]"><strong className="text-[#30244F]">Campos Customizados:</strong> Clique em "Novo Campo" para criar perguntas extras. Você pode editar e deletar livremente.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#8F7AC5] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                    <p className="text-[#6B5B8C]"><strong className="text-[#30244F]">Obrigatório:</strong> Marque campos com <span className="text-red-500">*</span> para exigir preenchimento.</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#D98DAE] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                    <p className="text-[#6B5B8C]"><strong className="text-[#30244F]">Ícone do Olho:</strong> <Eye className="w-4 h-4 inline" /> Ativa/desativa a visibilidade do campo no formulário.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#D98DAE] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">5</span>
                    <p className="text-[#6B5B8C]"><strong className="text-[#30244F]">Engrenagem:</strong> <Settings className="w-4 h-4 inline" /> Abre o editor para alterar label, placeholder e opções.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-[#D98DAE] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">6</span>
                    <p className="text-[#6B5B8C]"><strong className="text-[#30244F]">Salvar:</strong> Sempre clique em "Salvar" após fazer alterações!</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-[#E8D4D0]/50 flex items-center gap-6 text-xs text-[#9890A8]">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#8F7AC5]"></span>
                  Campo base (protegido)
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-[#D98DAE]"></span>
                  Campo customizado
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-500">*</span>
                  Obrigatório
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Botão Adicionar Campo */}
        <div className="mb-6 flex justify-end">
          <Button 
            onClick={() => setShowAddField(true)}
            className="bg-[#D98DAE] hover:bg-[#C77A9B] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Campo
          </Button>
        </div>

        {/* Etapas do Formulário */}
        <div className="space-y-4">
          {STEPS.map(step => (
            <motion.div
              key={step.value}
              className="bg-white rounded-2xl shadow-sm overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header da Etapa */}
              <button
                onClick={() => setExpandedStep(expandedStep === step.value ? null : step.value)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-[#8F7AC5]/10 to-transparent hover:from-[#8F7AC5]/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#8F7AC5] text-white flex items-center justify-center font-bold">
                    {step.value}
                  </span>
                  <span className="font-semibold text-[#30244F]">{step.label}</span>
                  <span className="text-sm text-[#6B5B8C] bg-[#FAE0D5]/50 px-2 py-0.5 rounded-full">
                    {getFieldsByStep(step.value).filter(f => f.active !== false).length} campos ativos
                  </span>
                </div>
                {expandedStep === step.value ? (
                  <ChevronUp className="w-5 h-5 text-[#6B5B8C]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#6B5B8C]" />
                )}
              </button>

              {/* Lista de Campos */}
              <AnimatePresence>
                {expandedStep === step.value && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-[#E8D4D0]"
                  >
                    <div className="p-4 space-y-2">
                      {getFieldsByStep(step.value).map((field, index) => (
                        <FieldCard
                          key={field.id || field.field_key}
                          field={field}
                          onUpdate={(updates) => updateField(field.id || field.field_key, updates)}
                          onDelete={() => deleteField(field.id)}
                          onEdit={() => setEditingField(field)}
                          getFieldTypeIcon={getFieldTypeIcon}
                        />
                      ))}
                      
                      {getFieldsByStep(step.value).length === 0 && (
                        <p className="text-center py-8 text-[#9890A8]">
                          Nenhum campo nesta etapa
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Modal Adicionar Campo */}
      <Dialog open={showAddField} onOpenChange={setShowAddField}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo Campo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome do Campo *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                placeholder="Ex: Observações adicionais"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={newField.field_type} 
                  onValueChange={(v) => setNewField({ ...newField, field_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <span className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Etapa</Label>
                <Select 
                  value={newField.step.toString()} 
                  onValueChange={(v) => setNewField({ ...newField, step: parseInt(v) })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STEPS.map(step => (
                      <SelectItem key={step.value} value={step.value.toString()}>
                        Etapa {step.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Placeholder (texto de ajuda)</Label>
              <Input
                value={newField.placeholder}
                onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                placeholder="Ex: Digite aqui..."
                className="mt-1"
              />
            </div>
            
            {['select', 'radio', 'checkbox'].includes(newField.field_type) && (
              <div>
                <Label>Opções (uma por linha)</Label>
                <Textarea
                  value={(newField.options || []).join('\n')}
                  onChange={(e) => setNewField({ 
                    ...newField, 
                    options: e.target.value.split('\n').filter(o => o.trim()) 
                  })}
                  placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                  className="mt-1"
                  rows={4}
                />
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <Label>Campo obrigatório?</Label>
              <Switch
                checked={newField.required}
                onCheckedChange={(v) => setNewField({ ...newField, required: v })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddField(false)}>
              Cancelar
            </Button>
            <Button onClick={addField} className="bg-[#8F7AC5] hover:bg-[#7B68A6] text-white">
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Campo */}
      <Dialog open={!!editingField} onOpenChange={() => setEditingField(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Campo</DialogTitle>
          </DialogHeader>
          
          {editingField && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Nome do Campo</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                  className="mt-1"
                  disabled={editingField.is_base_field}
                />
                {editingField.is_base_field && (
                  <p className="text-xs text-[#9890A8] mt-1">Campos base não podem ter o nome alterado</p>
                )}
              </div>
              
              <div>
                <Label>Placeholder</Label>
                <Input
                  value={editingField.placeholder || ''}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              {['select', 'radio', 'checkbox'].includes(editingField.field_type) && (
                <div>
                  <Label>Opções (uma por linha)</Label>
                  <Textarea
                    value={(editingField.options || []).join('\n')}
                    onChange={(e) => setEditingField({ 
                      ...editingField, 
                      options: e.target.value.split('\n').filter(o => o.trim()) 
                    })}
                    className="mt-1"
                    rows={4}
                  />
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <Label>Campo obrigatório?</Label>
                <Switch
                  checked={editingField.required}
                  onCheckedChange={(v) => setEditingField({ ...editingField, required: v })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Campo ativo?</Label>
                <Switch
                  checked={editingField.active !== false}
                  onCheckedChange={(v) => setEditingField({ ...editingField, active: v })}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingField(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                updateField(editingField.id || editingField.field_key, editingField);
                setEditingField(null);
              }} 
              className="bg-[#8F7AC5] hover:bg-[#7B68A6] text-white"
            >
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente Card de Campo
const FieldCard = ({ field, onUpdate, onDelete, onEdit, getFieldTypeIcon }) => {
  const Icon = getFieldTypeIcon(field.field_type);
  const isBaseField = field.is_base_field;
  const isActive = field.active !== false;
  
  return (
    <motion.div
      layout
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        isActive 
          ? 'bg-white border-[#E8D4D0] hover:border-[#8F7AC5]/50' 
          : 'bg-gray-50 border-gray-200 opacity-60'
      }`}
    >
      {/* Drag Handle */}
      <div className="cursor-move text-[#9890A8] hover:text-[#6B5B8C]">
        <GripVertical className="w-5 h-5" />
      </div>
      
      {/* Indicador de tipo */}
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        isBaseField ? 'bg-[#8F7AC5]/10' : 'bg-[#D98DAE]/10'
      }`}>
        <Icon className={`w-5 h-5 ${isBaseField ? 'text-[#8F7AC5]' : 'text-[#D98DAE]'}`} />
      </div>
      
      {/* Info do Campo */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-[#30244F] truncate">
            {field.label}
          </span>
          {field.required && (
            <span className="text-red-500 text-sm">*</span>
          )}
          {isBaseField && (
            <span className="text-xs bg-[#8F7AC5]/10 text-[#8F7AC5] px-2 py-0.5 rounded-full">
              base
            </span>
          )}
        </div>
        <p className="text-sm text-[#9890A8] truncate">
          {field.placeholder || FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
        </p>
      </div>
      
      {/* Ações */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onUpdate({ active: !isActive })}
          className="text-[#6B5B8C]"
          title={isActive ? 'Desativar' : 'Ativar'}
        >
          {isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-[#6B5B8C]"
          title="Editar"
        >
          <Settings className="w-4 h-4" />
        </Button>
        
        {!isBaseField && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-red-400 hover:text-red-600 hover:bg-red-50"
            title="Remover"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default AdminFormBuilderPage;
